import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Sparkles, BadgePercent } from "lucide-react";
import { useDemoModal } from "@/context/DemoModalContext";

const DASHBOARD_IMG = "/img/dashboard-preview.jpg";

export default function HeroSection() {
  const { openModal, openFreeModal } = useDemoModal();
  const reduce = useReducedMotion();

  // Respeta prefers-reduced-motion: sin desplazamientos, solo fundido.
  const rise = (y = 16, delay = 0) =>
    reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.4, delay } }
      : {
          initial: { opacity: 0, y },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.5, delay },
        };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-16 sm:pt-[72px]"
      style={{
        background:
          "linear-gradient(168deg, #EEEBFF 0%, #F5F3FF 28%, #FAFAFE 58%, #ffffff 100%)",
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-20 left-[10%] w-[400px] h-[400px] opacity-25 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(74,58,138,0.15) 0%, transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-10 right-[30%] w-[300px] h-[300px] opacity-20 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(201,162,39,0.12) 0%, transparent 65%)" }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-[1500px] grid-cols-1 items-center gap-10 px-4 sm:px-6 md:px-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-6 lg:py-0 xl:px-16 py-12 sm:py-16">
        <div className="flex flex-col items-start text-left max-w-[620px]">
          <motion.div {...rise(12, 0)}>
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2.5 rounded-full bg-white/80 border border-violet-light px-4 py-2 text-sm font-semibold text-violet-fuerte shadow-[0_2px_12px_-4px_rgba(74,58,138,0.1)] mb-8"
            >
              <Sparkles className="w-4 h-4 text-gold" />
              Software de gestión para iglesias
            </span>
          </motion.div>

          <motion.h1
            {...rise(20, 0.06)}
            data-testid="hero-title"
            className="font-display font-bold text-ink tracking-[-0.035em]"
            style={{ fontSize: "clamp(2.5rem, 5vw + 0.5rem, 4.25rem)", lineHeight: 1.04 }}
          >
            ¿Cómo va la gestión de{" "}
            <span className="text-violet-fuerte">la iglesia</span>?
          </motion.h1>

          <motion.p
            {...rise(14, 0.14)}
            data-testid="hero-subtitle"
            className="mt-6 max-w-[520px] text-xl leading-relaxed text-ink-2 md:text-[1.35rem]"
          >
            Cada iglesia es diferente, pero todas comparten los mismos
            desafíos. Nosotros los conocemos bien.
          </motion.p>

          <motion.p
            {...rise(12, 0.2)}
            data-testid="hero-feature-line"
            className="mt-4 max-w-[520px] text-[15px] leading-relaxed text-ink-3"
          >
            Miembros, finanzas, eventos y ministerios. Todo en un solo lugar, en
            español.
          </motion.p>

          <motion.div
            {...rise(14, 0.26)}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              onClick={openFreeModal}
              data-testid="hero-cta-primary"
              className="inline-flex h-[52px] cursor-pointer items-center justify-center gap-2 rounded-xl bg-violet-fuerte px-8 text-[17px] font-semibold text-white shadow-[0_6px_24px_-6px_rgba(74,58,138,0.45)] transition-all duration-300 hover:bg-violet-oscuro hover:shadow-[0_10px_32px_-6px_rgba(74,58,138,0.5)] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-fuerte focus-visible:ring-offset-2"
            >
              Empezar gratis
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={openModal}
              data-testid="hero-cta-secondary"
              className="group inline-flex cursor-pointer items-center gap-2 rounded-lg px-1 py-1 text-[17px] font-semibold text-ink-2 transition-colors duration-200 hover:text-violet-fuerte focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-fuerte focus-visible:ring-offset-2"
            >
              Probar demo
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: reduce ? 0 : 0.33 }}
            data-testid="hero-trust-line"
            style={{
              marginTop: "12px",
              fontSize: "13px",
              color: "#6B7280",
              fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
            }}
          >
            Plan gratuito permanente <span style={{ color: "#4B5563" }}>&middot;</span> Sin tarjeta{" "}
            <span style={{ color: "#4B5563" }}>&middot;</span> Sin compromiso
          </motion.p>

          <motion.div
            {...rise(10, 0.4)}
            data-testid="hero-founder-chip"
            className="mt-6"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-[#FFFBEA] px-4 py-2 text-[13px] font-semibold text-gold-text">
              <BadgePercent className="h-4 w-4 shrink-0 text-gold" strokeWidth={2} />
              Oferta Fundadores &middot; Precio bloqueado de por vida
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={reduce ? { opacity: 0 } : { opacity: 0, x: 50 }}
          animate={reduce ? { opacity: 1 } : { opacity: 1, x: 0 }}
          transition={{ duration: reduce ? 0.4 : 0.75, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:justify-self-end max-lg:mx-auto w-full"
          data-testid="hero-dashboard-preview"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-4 -z-10 rounded-3xl opacity-40 blur-[60px]"
            style={{ background: "radial-gradient(ellipse at 50% 70%, rgba(74,58,138,0.25) 0%, transparent 60%)" }}
          />
          <div className="relative lg:ml-6 xl:ml-0 lg:-mr-12 xl:-mr-20 2xl:-mr-28">
            <div className="rounded-2xl lg:rounded-r-none border border-violet-light/80 bg-white shadow-[0_40px_100px_-24px_rgba(74,58,138,0.16),0_8px_32px_-8px_rgba(74,58,138,0.08)] overflow-hidden">
              <div className="flex min-w-0 items-center gap-2 px-3 py-3.5 sm:gap-3 sm:px-5 bg-[#FAFAFE] border-b border-violet-light/60">
                <div className="flex shrink-0 gap-2">
                  <span className="w-3 h-3 rounded-full bg-[#FF6159]" />
                  <span className="w-3 h-3 rounded-full bg-[#FFBF2E]" />
                  <span className="w-3 h-3 rounded-full bg-[#29CC42]" />
                </div>
                <div className="flex min-w-0 flex-1 justify-center">
                  <div className="max-w-full truncate rounded-lg border border-violet-light/80 bg-white px-2 py-1 text-center text-xs font-medium text-ink-3 sm:px-5 sm:text-sm">
                    app.ekklesiaonline.app
                  </div>
                </div>
                <div className="w-10 shrink-0 sm:w-[52px]" aria-hidden />
              </div>
              <img
                src={DASHBOARD_IMG}
                alt="Panel de Ekklesia: gestión de iglesia con miembros, eventos y finanzas"
                className="block h-auto w-full"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
